import React, { FC, memo } from 'react';
import cls from './SendFileForMessage.module.scss';
import { CgAttachment } from 'react-icons/cg';
import routes from '../../../routes';
import { toast } from 'react-toastify';
import { bandittoApi } from '../../../store/api/api';
import { useAppDispatch } from '../../../hooks/defaultHooks';
import { useGetMessageFileSettingsQuery } from '../../message/message.service';

type SendFileForMessageProps = {
  consumerId: string;
  setShowFileLoad: React.Dispatch<React.SetStateAction<boolean>>;
  setFileInfo: React.Dispatch<
    React.SetStateAction<{
      name: string;
    }>
  >;
};

const UPLOAD_FAILED_MESSAGE = 'The file could not be uploaded';
// apimessages rejects an unsupported type or an oversized file, apisupport turns that into a 400;
// the multipart limit of apisupport itself answers with 413
const UPLOAD_REJECTED_MESSAGE = 'The file was rejected: check its format and size';
const UNSUPPORTED_TYPE_MESSAGE = 'This file format is not supported';
const TOO_LARGE_MESSAGE = 'The file is too large';

const getUploadErrorMessage = (status: number) =>
  status === 400 || status === 413 ? UPLOAD_REJECTED_MESSAGE : UPLOAD_FAILED_MESSAGE;

const SendFileForMessage: FC<SendFileForMessageProps> = memo((props) => {
  const { consumerId, setShowFileLoad, setFileInfo } = props;
  const dispatch = useAppDispatch();
  const { data: fileSettings } = useGetMessageFileSettingsQuery();
  // the settings may not have arrived yet - then the server stays the only judge
  const rejectReason = (file: File) => {
    if (!fileSettings) return null;
    if (!fileSettings.types.includes(file.type)) return UNSUPPORTED_TYPE_MESSAGE;
    if (file.size > fileSettings.maxSize) return TOO_LARGE_MESSAGE;
    return null;
  };
  const uploadHandler = async (e: React.ChangeEvent<HTMLInputElement>) => {
    try {
      if (e.target.files) {
        const file = e.target.files[0];
        const formData = new FormData();
        formData.append('file', file);
        formData.append('consumerId', consumerId);
        if (file) {
          const reason = rejectReason(file);
          if (reason) {
            toast.error(reason);
            return;
          }
          const accessToken = JSON.parse(localStorage.getItem('banditto') || '{}').accessToken;

          setShowFileLoad(true);
          setFileInfo({ name: file.name });
          const res = await fetch(`${routes.api.basePath()}${routes.api.uploadMessageFile()}`, {
            method: 'POST',
            body: formData,
            mode: 'cors',
            headers: {
              authorization: `Bearer ${accessToken}`,
            },
          });
          // fetch does not reject on 4xx/5xx, so a rejected upload has to be caught by hand
          if (!res.ok) {
            toast.error(getUploadErrorMessage(res.status));
            return;
          }
          // the upload bypasses RTK Query, so the chat has to be invalidated by hand
          dispatch(bandittoApi.util.invalidateTags(['Messages']));
        }
      }
    } catch {
      toast.error(UPLOAD_FAILED_MESSAGE);
    } finally {
      setShowFileLoad(false);
      setFileInfo({ name: '' });
      // let the same file be picked again after a rejection - onChange would stay silent otherwise
      e.target.value = '';
    }
  };
  return (
    <div className={cls.SendFileForMessage}>
      <label htmlFor="send-file">
        <CgAttachment size="24px" fill="currentColor" />
      </label>
      <input
        type="file"
        id="send-file"
        className={cls.SendFile}
        accept={fileSettings?.types.join(',')}
        onChange={(event) => uploadHandler(event)}
      />
    </div>
  );
});

export default SendFileForMessage;
